import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import {
  type ApiScopeRequirement,
  type OasDocument3XExtended,
  type OasWebPage,
  type OasWebPageGroup,
  type OasWebPageTreeNode,
  type OasWebTab,
  type OasWebTabsPage,
  createTableEndpointFromOas,
  isNonNullable,
  isOasWebMenuNode,
  isOasWebPageApiTable,
  isOasWebPageCustom,
  isOasWebTabsPage,
  narrowType,
  safeAbsurd,
} from '@ipfabric/utils';
import { ___, partialApply } from '@ipfabric/utils/fp/partialApply';
import type { RequiredBy } from '@ipfabric/utils/types/RequiredBy';
import classNames from 'classnames';
import { createApiTableRouteInfo } from 'components/ApiTablePage/createApiTableRouteInfo';
import {
  TabbedTables,
  type TableTabRoute,
} from 'components/TabbedTables/TabbedTables';
import type { RoutesTreeItem } from 'containers/App/Routes/RoutesTreeItem';
import { oasWebCustomPages } from 'containers/App/Routes/oas/oasWebCustomPages';
import { array } from 'fp-ts';
import { identity, pipe } from 'fp-ts/lib/function';
import { Outlet } from 'react-router-dom';
import { DefaultOasWebPages } from 'utils/oas/constant';
import { getOasOperationTable } from 'utils/oas/getOasOperationTable';
import { CloudLicensingGatedContent } from 'utils/pageGating/CloudLicensingGatedContent';
import { CLOUD_LICENSING_PAGE_PATH } from 'utils/pageGating/cloudLicensingPageGating';
import {
  LicenseGatedContent,
  type PageAccessRule,
  isPageStructurallyHidden,
  pageAccessRuleFromOasNode,
} from 'utils/pageGating/pageGating';
import { createTableApiScopeRequirement } from 'utils/rbac/helpers/createTableApiScopeRequirement';

const wrapRouteWithLicenseGate = (
  rule: PageAccessRule | undefined,
  route: RequiredBy<RoutesTreeItem, 'path'>,
): RequiredBy<RoutesTreeItem, 'path'> => {
  if (rule === undefined || rule.kind === 'feature-flag') {
    return route;
  }
  return {
    ...route,
    element: (
      <LicenseGatedContent feature={rule.licenseFeature}>
        {route.element}
      </LicenseGatedContent>
    ),
  };
};

/**
 * Gate the License overview page on the current snapshot supporting the new cloud licensing model.
 * Wrapping the tabs page element also covers its tab routes — they render through its `<Outlet>`.
 * NIM-24746.
 */
const wrapRouteWithCloudLicensingGate = (
  route: RequiredBy<RoutesTreeItem, 'path'>,
): RequiredBy<RoutesTreeItem, 'path'> =>
  route.path === CLOUD_LICENSING_PAGE_PATH
    ? {
        ...route,
        element: (
          <CloudLicensingGatedContent>
            {route.element}
          </CloudLicensingGatedContent>
        ),
      }
    : route;

const getTabApiScopeRequirement = (
  tab: OasWebTab,
): ApiScopeRequirement | undefined => {
  if (isOasWebPageApiTable(tab)) {
    return createTableApiScopeRequirement(tab.apiTable);
  }
  if (isOasWebPageCustom(tab)) {
    return tab.apiScopeRequirement;
  }
  if (isOasWebTabsPage(tab)) {
    // no need to determine scope for sub-tabs, TabbedTables handles hiding tab with all hidden children at runtime
    return undefined;
  }
  safeAbsurd(tab);
  return undefined;
};

const createTabsPageRouteFromOas = (
  oas: OasDocument3XExtended,
  group: OasWebPageGroup,
  tabsPage: OasWebTabsPage,
  insideTab: boolean,
): RequiredBy<RoutesTreeItem, 'path'> => {
  const tabRoutes = tabsPage.tabs
    .map((tab): TableTabRoute | null => {
      const route = createRouteFromTabOrPage(oas, group, tab, true);
      if (route === null) {
        return null;
      }
      return {
        ...route,
        default: tab.default,
        getTabLabel: () => tab.title,
        requiredApiScope: getTabApiScopeRequirement(tab),
      };
    })
    .filter(isNonNullable);
  const element = (
    <>
      {insideTab && null}
      <TabbedTables
        className={classNames(!insideTab && 'm')}
        tabs={tabRoutes}
        rootPath={tabsPage.path}
        layout={insideTab ? 'top-nested' : 'top'}
      />
    </>
  );
  return {
    path: tabsPage.path,
    element,
    children: tabRoutes,
  };
};

const wrapApiTablePageWithoutTabs = (
  route: RequiredBy<RoutesTreeItem, 'path'>,
): RequiredBy<RoutesTreeItem, 'path'> => ({
  path: route.path,
  element: (
    <div className='p flex flex-col flex-1'>
      <Outlet />
    </div>
  ),
  children: [route],
});

const createRouteFromTabOrPage = (
  oas: OasDocument3XExtended,
  group: OasWebPageGroup,
  node: OasWebTabsPage | OasWebPage,
  insideTab: boolean,
): RequiredBy<RoutesTreeItem, 'path'> | null => {
  const accessRule = pageAccessRuleFromOasNode(node);
  if (isPageStructurallyHidden(accessRule)) {
    return null;
  }

  if (isOasWebTabsPage(node)) {
    return wrapRouteWithCloudLicensingGate(
      wrapRouteWithLicenseGate(
        accessRule,
        createTabsPageRouteFromOas(oas, group, node, insideTab),
      ),
    );
  }

  if (isOasWebPageApiTable(node)) {
    const oasOperationTable = getOasOperationTable(oas, node.apiTable);
    if (oasOperationTable === null) {
      stumble(
        `Cannot create table page: API endpoint ${node.apiTable} does not contain table definition`,
      );
      return null;
    }
    const route = pipe(
      oasOperationTable,
      partialApply(
        createTableEndpointFromOas,
        node.apiTable,
        ___,
        isFeatureFlagEnabled('ENABLE_NEW_CLOUD_MODEL'),
      ),
      createApiTableRouteInfo(group),
      narrowType(
        (route: RoutesTreeItem): route is RequiredBy<typeof route, 'path'> =>
          route.path !== undefined,
      ),
      insideTab ? identity : wrapApiTablePageWithoutTabs,
    );
    return wrapRouteWithLicenseGate(accessRule, route);
  }

  if (isOasWebPageCustom(node)) {
    return wrapRouteWithLicenseGate(accessRule, {
      path: node.path,
      element: oasWebCustomPages[node.customPage],
    });
  }

  safeAbsurd(node);
  return null;
};

const createRoutesFromNode =
  (oas: OasDocument3XExtended, group: OasWebPageGroup) =>
  (node: OasWebPageTreeNode): RoutesTreeItem[] => {
    if (isOasWebMenuNode(node)) {
      const accessRule = pageAccessRuleFromOasNode(node);
      if (isPageStructurallyHidden(accessRule)) {
        return [];
      }
      return createRoutesFromNodeList(oas, group)(node.submenu);
    }
    return [createRouteFromTabOrPage(oas, group, node, false)].filter(
      isNonNullable,
    );
  };

const createRoutesFromNodeList =
  (oas: OasDocument3XExtended, group: OasWebPageGroup) =>
  (nodes: OasWebPageTreeNode[]): RoutesTreeItem[] =>
    array.chain(createRoutesFromNode(oas, group))(nodes);

export const createRoutesFromOas = (
  oas: OasDocument3XExtended,
  group: OasWebPageGroup,
): RoutesTreeItem[] => {
  const pageNodes = (oas['x-webPages'] ?? DefaultOasWebPages)[group];
  return createRoutesFromNodeList(oas, group)(pageNodes);
};
